<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="single" :class="{ loading: isLoading }">
    <div v-if="!isLoading" class="back-to-results">
      <a @click="back">Back to Results</a>
    </div>
    <div v-if="dataHotel && dataHotel.images" class="images">
      <img v-if="dataHotel.image_primary" class="hero" :src="dataHotel.image_primary" />
      <img v-if="dataHotel.images[0]" class="o1" :src="dataHotel.images[0]" />
      <img v-if="dataHotel.images[1]" class="o2" :src="dataHotel.images[1]" />
      <img v-if="dataHotel.images[2]" class="o3" :src="dataHotel.images[2]" />
      <img v-if="dataHotel.images[3]" class="o4" :src="dataHotel.images[3]" />
    </div>
    <h3>{{ dataHotel.name }}</h3>
    <div class="descriptions">
      <p v-for="(d, i) in dataHotel.descriptions" :key="i">
        {{ d }}
      </p>
    </div>
    <h3>Available Rooms</h3>
    <div v-if="dataHotel.details" class="rooms">
      <div v-for="(r, i) in roomsFiltered" :key="i" class="room">
        <div class="upper">
          <img v-if="r.image_primary" :src="r.image_primary" :class="{ none: r.image_primary == null }" />
          <div class="name">{{ r.name }}</div>
          <div v-for="(b, j) in r.bed_options[0].beds" :key="j" class="bed">{{ b.quantity }} {{ b.name }} {{ b.quantity == 1 ? "bed" : "beds" }}</div>
        </div>
        <div class="lower">
          <div class="rates">
            <div v-for="(p, ii) in r.prices" :key="ii" class="rate">
              <div class="radiolabel" :class="{ on: ii == r.price_selected_index }" @click="r.price_selected_index = ii">
                <div class="radio" />
                <label>
                  <span class="refundable" :class="{ yes: p.is_refundable }">
                    {{ p.is_refundable ? "Refundable" : "Non-Refundable" }}
                  </span>
                  <span>{{ roomRateDiff(r, ii) }}</span>
                </label>
              </div>
            </div>
          </div>
          <div class="price">
            <div class="amt">
              <span>{{ priceLabel(r, false) }}</span>
            </div>
            <div class="pol" :class="{ within: policyIs(r) }">
              {{ policyIs(r) ? "In Policy" : "Out of Policy" }}
            </div>
          </div>
          <div class="total">
            <span class="amt">{{ priceLabel(r, true) }} total</span>
            <span>incl. taxes &amp; fees</span>
          </div>
          <button @click="select(r)">Select</button>
        </div>
      </div>
      <div v-if="!isLoading && roomsFiltered.length == 0">No Rooms Available</div>
    </div>
    <h3>Instructions</h3>
    <div class="instructions">
      <div>Available check-in: {{ dataHotel.available_checkin }}</div>
      <div>Available check-out: {{ dataHotel.available_checkout }}</div>
    </div>
    <div class="instructions">
      <div v-for="(d, i) in dataHotel.instructions" :key="i" v-html="d" />
    </div>
    <a href="https://developer.expediapartnersolutions.com/terms/en" target="_blank">Terms &amp; Conditions</a>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import useApi from "../utils/apiCalls";
import DateTime from "../utils/datetime";

const emit = defineEmits(["error", "scroll", "update:id", "update:hotels", "update:isSavedHotels", "update:legIndex"]);
const props = defineProps(["id", "hotels", "legIndex", "organizationId"]);

const { utilDateISO8601 } = DateTime();
const { apiGetRequest } = useApi(emit);

// State
const dataHotel = ref({});
const isLoading = ref(true);

// Computed Properties
const roomsFiltered = computed(() => {
  let rooms = [];
  if (dataHotel.value.details) {
    rooms = dataHotel.value.details.rooms.filter(room => {
      return room.prices.length > 0 && room.prices[room.prices.length - 1].price_display > 0;
    });
  }
  return rooms;
});

// Methods
const back = () => {
  emit("update:id", null);
};

const load = () => {
  isLoading.value = true;

  const checkin = utilDateISO8601(props.hotels.route[props.legIndex].checkin);
  const checkout = utilDateISO8601(props.hotels.route[props.legIndex].checkout);
  const adults = props.hotels.route[props.legIndex].rooms.map(room => room.adults).join(",");

  let url = `/properties/${props.id}?checkin=${checkin}&checkout=${checkout}&adults=${adults}`;

  if (props.organizationId != null && props.organizationId > 0) {
    url += "&organization-id=" + props.organizationId;
  }

  apiGetRequest({
    path: url,
    onSuccess: data => {
      dataHotel.value = data;
      isLoading.value = false;
    },
    onError: error => {
      emit("error", error);
      isLoading.value = false;
    },
  });
};

const policyIs = r => {
  return r.prices[r.price_selected_index].is_policy_within;
};

const priceLabel = (r, isTotal) => {
  const priceObj = r.prices[r.price_selected_index];
  const price = isTotal ? priceObj.price_total : priceObj.price_display;
  const sym = r.price_currency_symbol ? r.price_currency_symbol : r.price_currency;
  return `${sym}${parseFloat(price).toFixed(2)}`;
};

const roomRateDiff = (r, i) => {
  if (i === 0 || i > r.prices.length - 1) {
    return "";
  } else {
    const r1 = r.prices[i - 1].price_display;
    const r2 = r.prices[i].price_display;
    return ` +${r.price_currency_symbol}${Math.round(r2 - r1)}`;
  }
};

const select = r => {
  // Set room
  dataHotel.value.room_name = r.name;

  // Set prices
  const selectedPrice = r.prices[r.price_selected_index];
  Object.assign(dataHotel.value, selectedPrice);

  // Set policy
  dataHotel.value.is_policy_within = selectedPrice.is_policy_within;
  dataHotel.value.is_policy_approval = selectedPrice.is_policy_approval;

  // Create new object
  const newHotels = props.hotels;
  newHotels.route[props.legIndex].ideal = { ...dataHotel.value };
  newHotels.route[props.legIndex].ideal.checkin = props.hotels.route[props.legIndex].checkin;
  newHotels.route[props.legIndex].ideal.checkout = props.hotels.route[props.legIndex].checkout;
  newHotels.route[props.legIndex].ideal.rooms = props.hotels.route[props.legIndex].rooms;

  emit("update:hotels", newHotels);
  const isLastLeg = props.legIndex === props.hotels.route.length - 1;
  const isEditing = "checkin" in props.hotels.route[props.hotels.route.length - 1].ideal;

  if (isLastLeg || isEditing) {
    emit("update:isSavedHotels", true);
  } else {
    emit("update:legIndex", props.legIndex + 1);
  }
  emit("scroll");
};

// Watchers
watch(() => props.id, load);

// Lifecycle hook
onMounted(load);
</script>

<style lang="less">
#hotels {
  .single {
    margin-bottom: 2rem;

    .back-to-results {
      margin-bottom: 1rem;
    }

    .images {
      margin-bottom: 2rem;
      width: 100%;

      img {
        display: block;
        object-fit: cover;
        object-position: bottom;
        width: 100%;
      }

      .o1,
      .o2,
      .o3,
      .o4 {
        display: none;
      }
    }

    .descriptions {
      margin-bottom: 2rem;

      p {
        color: @dark;
        margin-bottom: 1rem;
      }
    }

    .instructions {
      margin-bottom: 2rem;

      ul {
        margin-left: 1rem;
      }
    }

    .name {
      margin-bottom: 0.5rem;
    }

    .rooms {
      margin-bottom: 2rem;
    }

    .room {
      background: @light;
      border-bottom: 0.125rem solid #e7e7e7;
      border-right: 0.125rem solid #e7e7e7;
      margin-top: 1rem;
      padding: 1rem;
      position: relative;

      img {
        display: block;
        height: 10rem;
        margin: 0 auto 1rem;
        object-fit: cover;
        width: 100%;
      }
    }

    .upper {
      margin-bottom: 2rem;
    }

    .bed {
      font-size: 0.875rem;
      margin-top: 0.25rem;
    }

    .lower {
      button {
        margin-top: 1rem;
        width: 100%;
      }
    }

    .rates {
      font-size: 0.875rem;
      margin-bottom: 1rem;
    }

    .rate {
      margin-bottom: 0.25rem;
    }

    .refundable {
      color: @secondary;

      &.yes {
        color: @green;
      }
    }

    .price {
      display: flex;

      .amt {
        font-size: 1.125rem;
        flex: 1;
      }
    }

    .total {
      display: flex;
      color: @mid;
      margin-bottom: 1rem;

      .amt {
        flex: 1;
      }
    }

    .pol {
      color: @secondary;

      &.within {
        color: @green;
      }
    }
  }
}

@media screen and (min-width: 60em) {
  #hotels {
    .single {
      .images {
        display: grid;
        gap: 0.125rem;
        grid-auto-flow: row;
        grid-template-columns: repeat(4, 1fr);
        grid-template-rows: repeat(2, 1fr);
        grid-template-areas:
          "hero hero o1 o2"
          "hero hero o3 o4";

        .hero {
          grid-area: hero;
          height: 20.125rem;
        }

        .o1,
        .o2,
        .o3,
        .o4 {
          display: block;
        }

        .o1 {
          grid-area: o1;
          height: 10rem;
        }

        .o2 {
          grid-area: o2;
          height: 10rem;
        }

        .o3 {
          grid-area: o3;
          height: 10rem;
        }

        .o4 {
          grid-area: o4;
          height: 10rem;
        }
      }

      .rooms {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 2rem;
      }

      .room {
        display: grid;
        grid-template-rows: 1fr auto;
        margin-top: 0;
      }
    }
  }
}
</style>
